import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { injectReducer } from 'store'
import reducer from './store'
import { useDispatch, useSelector } from 'react-redux'
import { getData, setTableData } from './store/dataSlice'
import {
    HiOutlineCheckCircle,
    HiOutlineEye,
    HiOutlineTrash,
    HiOutlineXCircle,
} from 'react-icons/hi'
import Tooltip from 'components/Ui/Tooltip'
import { ConfirmationModal, Modal, Table } from 'components/Ui'
import { deleteApiRecord, sendMailApi } from 'services/InvoiceService'
import { Success } from 'utils/alerts'
import useAuth from 'utils/hooks/useAuth'
import { numberFormat } from 'components/helper'
import dayjs from 'dayjs'
import { RiHistoryLine, RiMailSendLine } from 'react-icons/ri'
import { MdOutlinePending } from 'react-icons/md'
import { ApproveRejectRecordApi } from 'services/RateCardService'
import { getApiUser } from 'services/TransportProviderService'
import { Field, Form, Formik } from 'formik'
import { Button, Select } from 'components/Form'
import * as Yup from 'yup'
import { FiAlertCircle } from 'react-icons/fi'
injectReducer('verifiedInvoices', reducer)

const validationSchema = Yup.object().shape({
    user_id: Yup.string().required('Please select user'),
})

export default function VerifiedInvoices() {
    const dispatch = useDispatch()
    const { checkPermission } = useAuth()
    const { data, tableData, loading } = useSelector(
        (state) => state.verifiedInvoices.data,
    )
    const [invoice, setInvoice] = useState('')
    const [open, setOpen] = useState(false)
    const [vendorData, setVendorData] = useState([])
    const [confirm, setConfirm] = useState(false)
    const [user, setUser] = useState('')
    const [message, setMessage] = useState('')

    useEffect(() => {
        fetchData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        dispatch,
        tableData.sort,
        tableData.query,
        tableData.pageIndex,
        tableData.pageSize,
        tableData.selectedTab,
        tableData.checkBox,
        tableData.service,
    ])

    const fetchData = () => dispatch(getData({ ...tableData }))

    const getUserData = (datas) => {
        const data = {
            transport_provider: datas?.transport_provider,
        }
        if (datas?.company) {
            data.company = datas?.company
        }
        getApiUser(data)
            .then((response) => {
                let arr = [
                    ...response?.data?.transport_admin,
                    ...response?.data?.transport_user,
                ]
                setVendorData(
                    arr.map((item) => {
                        item.label = `${item?.title}. ${item?.user?.first_name} ${item.user.last_name}`
                        item.value = item.user.id
                        return item
                    }),
                )
            })
            .catch((error) => error)
    }

    const handleStatus = (id, data) => {
        ApproveRejectRecordApi(id, data)
            .then(() => {
                Success(
                    `Invoice moved to ${data.status} Invoices Successfully!`,
                )
                fetchData()
            })
            .catch((error) => error)
    }

    const handleDelete = (id) => {
        deleteApiRecord(id)
            .then(() => {
                Success('Deleted Successfully!')
                fetchData()
            })
            .catch((error) => error)
    }

    const handleSubmit = (values, { setSubmitting }) => {
        setSubmitting(true)
        sendMailApi({ invoice_id: invoice, ...values })
            .then(() => {
                setSubmitting(false)
                setOpen(false)
                Success('Email Sent Successfully!')
            })
            .catch((error) => {
                setSubmitting(false)
                setOpen(false)
                setConfirm(true)
                setMessage(error?.response?.data?.error)
                return error
            })
    }

    const columns = useMemo(
        () => [
            {
                field: 'invoice',
                header: 'Invoice',
                className: 'min-w-[150px]',
                renderCell: (row) =>
                    row?.invoice_id?.invoice.split('/')[
                        row?.invoice_id?.invoice.split('/').length - 1
                    ],
            },
            {
                field: 'invoice_num',
                header: 'Invoice Number',
                className: 'min-w-[150px]',
            },
            {
                field: 'invoiced_total',
                header: 'Invoice Total',
                className: 'min-w-[150px]',
                renderCell: (row) => numberFormat(row?.invoiced_total, 'AUD'),
            },
            {
                field: 'total_line_items',
                header: 'Total Line Items',
                className: 'min-w-[150px]',
            },
            {
                field: 'total_verified_line_items',
                header: 'Total Verified Line Items',
                className: 'min-w-[150px]',
            },
            {
                field: 'total_overcharged',
                header: 'Total Overcharged (Amount)',
                className: 'min-w-[150px]',
                renderCell: (row) =>
                    numberFormat(row?.total_overcharged, 'AUD'),
            },
            {
                field: 'total_undercharged',
                header: 'Total Undercharged (Amount)',
                className: 'min-w-[150px]',
                renderCell: (row) =>
                    numberFormat(row?.total_undercharged, 'AUD'),
            },
            {
                field: 'uploaded_at',
                header: 'Uploaded At',
                className: 'min-w-[150px]',
                renderCell: (row) =>
                    row?.invoice_id?.uploaded_at
                        ? dayjs(row?.invoice_id?.uploaded_at).format(
                              'DD-MMM-YYYY',
                          )
                        : '-',
            },
            {
                header: 'Action',
                field: 'action',
                className: 'px-4',
                renderCell: (row) => {
                    return (
                        <div className="flex items-center gap-3">
                            <Tooltip title={'View'}>
                                <Link
                                    to={`/invoices/verified-invoices/${row.invoice_num}/result`}
                                    className="hover:text-primary"
                                >
                                    <HiOutlineEye size={20} />
                                </Link>
                            </Tooltip>
                            <Tooltip
                                title="View Invoice History"
                                placement="top"
                            >
                                <Link
                                    to={`/invoices/verified-invoices/${row.invoice_num}/invoice-history`}
                                    className="hover:text-primary"
                                >
                                    <RiHistoryLine size={20} />
                                </Link>
                            </Tooltip>
                            <Tooltip title={'Send Mail'} placement={'top'}>
                                <span
                                    className={`cursor-pointer hover:text-primary`}
                                    onClick={() => {
                                        getUserData({
                                            transport_provider:
                                                row?.assignment
                                                    ?.transport_provider,
                                            company:
                                                row?.assignment?.company_id,
                                        })
                                        setInvoice(row?.id)
                                        setOpen(true)
                                    }}
                                >
                                    <RiMailSendLine size={20} />
                                </span>
                            </Tooltip>
                            <Tooltip title={'Pending'} placement={'top'}>
                                <ConfirmationModal
                                    message={
                                        'Are you sure you want to move this invoice into pending invoices!'
                                    }
                                    onConfirm={() => {
                                        handleStatus(row.id, {
                                            status: 'Pending',
                                            invoice_num: row.invoice_num,
                                        })
                                    }}
                                >
                                    <span
                                        className={`cursor-pointer hover:text-primary`}
                                    >
                                        <MdOutlinePending size={20} />
                                    </span>
                                </ConfirmationModal>
                            </Tooltip>
                            <Tooltip title={'Approve'} placement={'top'}>
                                <ConfirmationModal
                                    message={
                                        'Are you sure you want to move this invoice into approved invoices!'
                                    }
                                    onConfirm={() => {
                                        handleStatus(row.id, {
                                            status: 'Approved',
                                            invoice_num: row.invoice_num,
                                        })
                                    }}
                                >
                                    <span
                                        className={`cursor-pointer hover:text-primary`}
                                    >
                                        <HiOutlineCheckCircle size={20} />
                                    </span>
                                </ConfirmationModal>
                            </Tooltip>
                            <Tooltip title={'Reject'} placement={'top'}>
                                <ConfirmationModal
                                    message={
                                        'Are you sure you want to move this invoice into rejected invoices!'
                                    }
                                    onConfirm={() => {
                                        handleStatus(row.id, {
                                            status: 'Rejected',
                                            invoice_num: row.invoice_num,
                                        })
                                    }}
                                >
                                    <span
                                        className={`cursor-pointer hover:text-danger`}
                                    >
                                        <HiOutlineXCircle size={20} />
                                    </span>
                                </ConfirmationModal>
                            </Tooltip>
                            {checkPermission('extra-charges', 'delete') ? (
                                <Tooltip title={'Delete'}>
                                    <ConfirmationModal
                                        message={
                                            'Are you sure you want to delete this record!'
                                        }
                                        onConfirm={() => {
                                            handleDelete(row.id)
                                        }}
                                    >
                                        <span className="hover:text-danger">
                                            <HiOutlineTrash size={20} />
                                        </span>
                                    </ConfirmationModal>
                                </Tooltip>
                            ) : null}
                        </div>
                    )
                },
            },
        ],
        // eslint-disable-next-line
        [
            tableData.sort,
            tableData.query,
            tableData.pageIndex,
            tableData.pageSize,
            tableData.selectedTab,
            tableData.checkBox,
            tableData.service,
        ],
    )

    return (
        <>
            <div className="flex flex-col gap-1 mb-5 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="font-semibold text-black text-title-md2 dark:text-white">
                    Verified Invoices
                </h2>
            </div>
            <Table
                loading={loading}
                filter={false}
                datas={data}
                columns={columns}
                // add_new={checkPermission('extra-charges', 'create') ? '/master-data/extra-charges/create' : ''}
                pagingData={tableData}
                onChange={(e) => dispatch(setTableData(e))}
            />
            <Modal
                show={open}
                onClose={() => {
                    setOpen(false)
                }}
            >
                <div className="flex flex-col gap-1 m-3">
                    <h2 className="font-semibold text-black text-title-md">
                        {'Send Email'}
                    </h2>
                </div>
                <div className="flex flex-col items-center">
                    <div className="w-full bg-white border rounded-sm overflow-auto max-h-150 border-stroke shadow-default dark:border-strokedark dark:bg-boxdark">
                        <div className="flex flex-col gap-3 p-6">
                            <Formik
                                initialValues={{ user_id: '', status: '0' }}
                                validationSchema={validationSchema}
                                onSubmit={handleSubmit}
                            >
                                {({ values, touched, errors }) => (
                                    <Form>
                                        <div className="grid grid-cols-1 gap-x-4 gap-y-4">
                                            <Field name="user_id">
                                                {({ field, form }) => (
                                                    <Select
                                                        options={vendorData}
                                                        label="Select User"
                                                        size="sm"
                                                        placeholder="Select"
                                                        name="user_id"
                                                        // className="md:col-span-2"
                                                        error={
                                                            errors.user_id &&
                                                            touched.user_id
                                                        }
                                                        message={errors.user_id}
                                                        value={values?.user_id}
                                                        onChange={(e) => {
                                                            form.setFieldValue(
                                                                field.name,
                                                                e.target.value,
                                                            )
                                                            setUser(
                                                                e.target.value,
                                                            )
                                                        }}
                                                    />
                                                )}
                                            </Field>
                                        </div>
                                        <div className="flex items-center justify-end gap-4 mt-3">
                                            <Button
                                                className="h-10 w-18 text-[#F05540] border border-[#F05540] dark:text-[#F05540] dark:border-[#F05540]"
                                                onClick={() => setOpen(false)}
                                                type="button"
                                            >
                                                Discard
                                            </Button>
                                            <Button
                                                type="submit"
                                                className="flex items-center justify-center px-6 py-2 text-white transition border rounded-lg cursor-pointer hover:bg-opacity-90 border-[#F05540] bg-[#F05540] dark:border-[#F05540] dark:bg-[#F05540]"
                                            >
                                                Save
                                            </Button>
                                        </div>
                                    </Form>
                                )}
                            </Formik>
                        </div>
                    </div>
                </div>
            </Modal>
            <Modal
                show={confirm}
                onClose={() => setConfirm(false)}
                maxWidth="md"
            >
                <div className="p-6 text-center">
                    <span className="inline-block mx-auto">
                        <div className="flex items-center justify-center w-20 h-20 transition rounded-full bg-warning/10 text-warning">
                            <FiAlertCircle size={40} />
                        </div>
                    </span>
                    <h3 className="pb-2 mt-5 text-xl font-bold text-black dark:text-white sm:text-2xl">
                        {'Confirmation Alert!'}
                    </h3>
                    <p className={`font-medium mb-10`}>
                        {message
                            ? message
                            : 'Are you sure you want to send mail?'}
                    </p>
                    <div className="flex flex-wrap mx-5 gap-y-4">
                        <div className="w-full px-3 xsm:w-1/2">
                            <Button
                                className="w-full text-white border-primary bg-primary dark:border-secondary dark:bg-secondary"
                                onClick={() => {
                                    setConfirm(false)
                                    sendMailApi({
                                        invoice_id: invoice,
                                        status: '1',
                                        user_id: user,
                                    })
                                        .then(() => {
                                            Success('Email Sent Successfully!')
                                        })
                                        .catch((error) => {
                                            setConfirm(false)
                                            return error
                                        })
                                }}
                            >
                                Yes
                            </Button>
                        </div>
                        <div className="w-full px-3 xsm:w-1/2">
                            <Button
                                type="button"
                                className="w-full text-white border-danger bg-danger"
                                onClick={() => setConfirm(false)}
                            >
                                No
                            </Button>
                        </div>
                    </div>
                </div>
            </Modal>
        </>
    )
}
